import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { xpConfigurationsService } from '../services/api';

const XpConfigurations = () => {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await xpConfigurationsService.getAll();
      setConfigurations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNew = () => {
    navigate('/xp-configurations/new');
  };

  const handleEdit = (configuration) => {
    navigate(`/xp-configurations/edit/${configuration.id}`);
  };

  const handleToggle = async (id) => {
    try {
      await xpConfigurationsService.toggle(id);
      fetchData();
    } catch (error) {
      console.error('Erreur lors du basculement:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration ?')) {
      try {
        await xpConfigurationsService.delete(id);
        fetchData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const filteredConfigurations = configurations.filter(config =>
    config.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.actionType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedConfigurations = filteredConfigurations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return <LoadingSpinner message="Chargement des configurations XP..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Configuration des XP
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
        >
          Nouvelle Configuration
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher une configuration..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type d'action</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>XP de base</TableCell>
              <TableCell>Type de calcul</TableCell>
              <TableCell>Limites</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedConfigurations.map((config) => (
              <TableRow key={config.id}>
                <TableCell>
                  <Chip
                    label={config.actionType}
                    color="primary"
                    size="small"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {config.displayName}
                  </Typography>
                  {config.description && (
                    <Typography variant="caption" color="textSecondary">
                      {config.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {config.baseXp} XP
                  </Typography>
                </TableCell>
                <TableCell>
                  {config.calculationType === 'fixed' ? (
                    <Chip label="Fixe" size="small" />
                  ) : (
                    <Box>
                      <Chip label={`${config.percentageValue}%`} size="small" color="secondary" />
                      {config.minXp && (
                        <Typography variant="caption" display="block">
                          Min: {config.minXp} XP
                        </Typography>
                      )}
                      {config.maxXp && (
                        <Typography variant="caption" display="block">
                          Max: {config.maxXp} XP
                        </Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {config.dailyLimit && (
                    <Typography variant="caption" display="block">
                      Jour: {config.dailyLimit}x
                    </Typography>
                  )}
                  {config.monthlyLimit && (
                    <Typography variant="caption" display="block">
                      Mois: {config.monthlyLimit}x
                    </Typography>
                  )}
                  {!config.dailyLimit && !config.monthlyLimit && (
                    <Chip label="Illimité" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={config.isActive ? 'Actif' : 'Inactif'}
                    color={config.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleToggle(config.id)}>
                    {config.isActive ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                  </IconButton>
                  <IconButton size="small" onClick={() => handleEdit(config)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(config.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredConfigurations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default XpConfigurations;















